/**
 * Settings Panel Component
 * User preferences for account, chat, game, and privacy settings
 */

import { authService } from '../../auth';

export interface SettingsData {
  chatFilterEnabled: boolean;
  defaultBoardSize: 5 | 7 | 9;
  soundEnabled: boolean;
  appearOffline: boolean;
  blockFriendRequests: boolean;
}

export interface SettingsPanelConfig {
  onClose: () => void;
  onSettingsChange?: (settings: Partial<SettingsData>) => void;
}

const DEFAULT_SETTINGS: SettingsData = {
  chatFilterEnabled: true,
  defaultBoardSize: 7,
  soundEnabled: true,
  appearOffline: false,
  blockFriendRequests: false,
};

export class SettingsPanel {
  private container: HTMLElement;
  private config: SettingsPanelConfig;
  private settings: SettingsData;
  private isModal: boolean;

  constructor(container: HTMLElement, config: SettingsPanelConfig, isModal = true) {
    this.container = container;
    this.config = config;
    this.isModal = isModal;
    this.settings = this.loadSettings();
    this.render();
  }

  /**
   * Load settings from localStorage
   */
  private loadSettings(): SettingsData {
    try {
      const stored = localStorage.getItem('pathgame_settings');
      if (stored) {
        return { ...DEFAULT_SETTINGS, ...JSON.parse(stored) };
      }
    } catch {
      console.warn('Failed to load settings');
    }
    return { ...DEFAULT_SETTINGS };
  }

  /**
   * Save settings to localStorage
   */
  private saveSettings(): void {
    try {
      localStorage.setItem('pathgame_settings', JSON.stringify(this.settings));
      this.config.onSettingsChange?.(this.settings);
    } catch {
      console.warn('Failed to save settings');
    }
  }

  /**
   * Get settings
   */
  getSettings(): SettingsData {
    return { ...this.settings };
  }

  /**
   * Render the settings panel
   */
  render(): void {
    const user = authService.getCurrentUser();
    const isLoggedIn = !!user;

    const panelContent = `
      <div class="settings-header">
        <h2 class="settings-title">Settings</h2>
        <button class="settings-close-btn" aria-label="Close">
          <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
            <line x1="18" y1="6" x2="6" y2="18"/>
            <line x1="6" y1="6" x2="18" y2="18"/>
          </svg>
        </button>
      </div>

      <div class="settings-body">
        <!-- Account Section -->
        <div class="settings-section">
          <h3 class="settings-section-title">
            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
              <path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2"/>
              <circle cx="12" cy="7" r="4"/>
            </svg>
            Account
          </h3>
          <div class="settings-section-content">
            ${isLoggedIn ? `
              <div class="settings-user-info">
                ${user?.avatarUrl 
                  ? `<img src="${user.avatarUrl}" alt="${user.username}" class="settings-avatar" />`
                  : `<div class="settings-avatar-placeholder">${user?.username?.charAt(0)?.toUpperCase() || '?'}</div>`
                }
                <div class="settings-user-details">
                  <span class="settings-username">${user?.displayName || user?.username}</span>
                  <span class="settings-email">${user?.email || (user?.isGuest ? 'Guest Account' : 'No email')}</span>
                </div>
              </div>
              ${user?.isGuest ? `
                <button class="settings-btn settings-btn-primary" id="upgrade-account-btn">
                  Upgrade to Full Account
                </button>
              ` : ''}
              <button class="settings-btn settings-btn-danger" id="sign-out-btn">
                Sign Out
              </button>
            ` : `
              <p class="settings-info-text">Sign in to sync your settings and access online features.</p>
              <button class="settings-btn settings-btn-primary" id="sign-in-btn">
                Sign In
              </button>
            `}
          </div>
        </div>

        <!-- Chat Preferences -->
        <div class="settings-section">
          <h3 class="settings-section-title">
            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
              <path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z"/>
            </svg>
            Chat
          </h3>
          <div class="settings-section-content">
            <label class="settings-toggle">
              <input type="checkbox" id="chat-filter-toggle" ${this.settings.chatFilterEnabled ? 'checked' : ''} />
              <span class="toggle-slider"></span>
              <span class="toggle-label">Content Filter</span>
              <span class="toggle-description">Filter inappropriate messages</span>
            </label>
          </div>
        </div>

        <!-- Game Preferences -->
        <div class="settings-section">
          <h3 class="settings-section-title">
            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
              <rect x="2" y="2" width="20" height="20" rx="2"/>
              <path d="M7 7h.01M12 12h.01M17 17h.01M7 17h.01M17 7h.01"/>
            </svg>
            Game
          </h3>
          <div class="settings-section-content">
            <div class="settings-option">
              <label for="default-board-size">Default Board Size</label>
              <select id="default-board-size" class="settings-select">
                <option value="5" ${this.settings.defaultBoardSize === 5 ? 'selected' : ''}>5×5 (Quick)</option>
                <option value="7" ${this.settings.defaultBoardSize === 7 ? 'selected' : ''}>7×7 (Standard)</option>
                <option value="9" ${this.settings.defaultBoardSize === 9 ? 'selected' : ''}>9×9 (Large)</option>
              </select>
            </div>

            <label class="settings-toggle">
              <input type="checkbox" id="sound-toggle" ${this.settings.soundEnabled ? 'checked' : ''} />
              <span class="toggle-slider"></span>
              <span class="toggle-label">Sound Effects</span>
              <span class="toggle-description">Play sounds for moves and game events</span>
            </label>
          </div>
        </div>

        <!-- Privacy Settings -->
        <div class="settings-section">
          <h3 class="settings-section-title">
            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
              <rect x="3" y="11" width="18" height="11" rx="2" ry="2"/>
              <path d="M7 11V7a5 5 0 0 1 10 0v4"/>
            </svg>
            Privacy
          </h3>
          <div class="settings-section-content">
            <label class="settings-toggle ${!isLoggedIn ? 'disabled' : ''}">
              <input type="checkbox" id="appear-offline-toggle" 
                ${this.settings.appearOffline ? 'checked' : ''} 
                ${!isLoggedIn ? 'disabled' : ''} />
              <span class="toggle-slider"></span>
              <span class="toggle-label">Appear Offline</span>
              <span class="toggle-description">Hide your online status from friends</span>
            </label>

            <label class="settings-toggle ${!isLoggedIn ? 'disabled' : ''}">
              <input type="checkbox" id="block-requests-toggle" 
                ${this.settings.blockFriendRequests ? 'checked' : ''} 
                ${!isLoggedIn ? 'disabled' : ''} />
              <span class="toggle-slider"></span>
              <span class="toggle-label">Block Friend Requests</span>
              <span class="toggle-description">Prevent new friend requests</span>
            </label>
          </div>
        </div>
      </div>
    `;

    if (this.isModal) {
      this.container.innerHTML = `
        <div class="settings-overlay">
          <div class="settings-panel">
            ${panelContent}
          </div>
        </div>
      `;
    } else {
      this.container.innerHTML = `
        <div class="settings-panel inline">
          ${panelContent}
        </div>
      `;
    }

    this.attachEventListeners();
  }

  /**
   * Attach event listeners
   */
  private attachEventListeners(): void {
    // Close button
    const closeBtn = this.container.querySelector('.settings-close-btn');
    closeBtn?.addEventListener('click', () => this.config.onClose());

    // Overlay click to close
    if (this.isModal) {
      const overlay = this.container.querySelector('.settings-overlay');
      overlay?.addEventListener('click', (e) => {
        if (e.target === overlay) {
          this.config.onClose();
        }
      });
    }

    // Sign in button
    const signInBtn = this.container.querySelector('#sign-in-btn');
    signInBtn?.addEventListener('click', () => {
      const { authUI } = require('../../auth');
      authUI.showLoginModal();
    });

    // Sign out button
    const signOutBtn = this.container.querySelector('#sign-out-btn');
    signOutBtn?.addEventListener('click', async () => {
      await authService.signOut();
      this.render();
    });

    // Upgrade account button
    const upgradeBtn = this.container.querySelector('#upgrade-account-btn');
    upgradeBtn?.addEventListener('click', () => {
      const { authUI } = require('../../auth');
      authUI.showConvertGuestModal();
    });

    // Chat filter toggle
    const chatFilterToggle = this.container.querySelector('#chat-filter-toggle') as HTMLInputElement;
    chatFilterToggle?.addEventListener('change', () => {
      this.settings.chatFilterEnabled = chatFilterToggle.checked;
      this.saveSettings();
    });

    // Default board size
    const boardSizeSelect = this.container.querySelector('#default-board-size') as HTMLSelectElement;
    boardSizeSelect?.addEventListener('change', () => {
      this.settings.defaultBoardSize = parseInt(boardSizeSelect.value) as 5 | 7 | 9;
      this.saveSettings();
    });

    // Sound toggle
    const soundToggle = this.container.querySelector('#sound-toggle') as HTMLInputElement;
    soundToggle?.addEventListener('change', () => {
      this.settings.soundEnabled = soundToggle.checked;
      this.saveSettings();
    });

    // Appear offline toggle
    const appearOfflineToggle = this.container.querySelector('#appear-offline-toggle') as HTMLInputElement;
    appearOfflineToggle?.addEventListener('change', () => {
      this.settings.appearOffline = appearOfflineToggle.checked;
      this.saveSettings();
    });

    // Block friend requests toggle
    const blockRequestsToggle = this.container.querySelector('#block-requests-toggle') as HTMLInputElement;
    blockRequestsToggle?.addEventListener('change', () => {
      this.settings.blockFriendRequests = blockRequestsToggle.checked;
      this.saveSettings();
    });
  }

  /**
   * Show the panel
   */
  show(): void {
    this.render();
  }

  /**
   * Hide the panel
   */
  hide(): void {
    this.container.innerHTML = '';
  }

  /**
   * Destroy
   */
  destroy(): void {
    this.container.innerHTML = '';
  }
}

/**
 * Create settings panel
 */
export function createSettingsPanel(container: HTMLElement, config: SettingsPanelConfig): SettingsPanel {
  return new SettingsPanel(container, config);
}

/**
 * Get current settings (static helper)
 */
export function getSettings(): SettingsData {
  try {
    const stored = localStorage.getItem('pathgame_settings');
    if (stored) {
      return { ...DEFAULT_SETTINGS, ...JSON.parse(stored) };
    }
  } catch {
    // Ignore
  }
  return { ...DEFAULT_SETTINGS };
}
